import React, { useState } from 'react';
import { TextField, useTheme } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import {
  CalendarInputContainer,
  InputLabelContainer,
  TextFieldArea,
} from './styles';
import { InputError } from '../typography';

export interface ICalendarInputProps {
  name: string;
  labelValue: string;
  value: string | null;
  placeholder: string;
  validationError?: string;
  hasError?: boolean;
  disabled?: boolean;
  className?: string;
  onChange: (value: Date | null) => void;
  maxDate?: Date;
  minDate?: Date;
  useBlackColor?: boolean;
}

const CalendarInput = ({
  labelValue,
  name,
  value,
  placeholder,
  disabled,
  onChange,
  className,
  validationError,
  hasError,
  maxDate,
  minDate,
  useBlackColor,
}: ICalendarInputProps) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const handleInputClick = () => {
    setIsOpen((state) => !state);
  };

  const handleCalendarClose = () => {
    setIsOpen(false);
  };

  const getDefaultCalendarMonth = () => {
    if (value) {
      const parsedValueDate = new Date(value);
      if (!Number.isNaN(parsedValueDate.getTime())) {
        return parsedValueDate;
      }
    }

    if (maxDate) {
      return maxDate;
    }

    if (minDate) {
      return minDate;
    }

    return undefined;
  };

  return (
    <CalendarInputContainer disabled={disabled} useBlackColor={useBlackColor}>
      {labelValue && <InputLabelContainer>{labelValue}</InputLabelContainer>}
      <div style={{ marginTop: '10px' }} className="text-field-area">
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          <DesktopDatePicker
            open={isOpen}
            label={null}
            inputFormat="dd/MM/yyyy"
            value={value ? value : null}
            onChange={(newValue) => {
              if (
                newValue === null ||
                (newValue instanceof Date && !isNaN(newValue.getTime()))
              ) {
                onChange(newValue);
              }
            }}
            maxDate={maxDate}
            minDate={minDate}
            defaultCalendarMonth={getDefaultCalendarMonth()}
            className={className}
            disabled={disabled}
            PaperProps={{
              sx: {
                '& .MuiCalendarOrClockPicker-root': {
                  '& .MuiCalendarPicker-root': {
                    '& .MuiPickersCalendarHeader-root': {
                      '& .MuiPickersCalendarHeader-labelContainer': {
                        backgroundColor: '#FDFDFD',
                        border: '1px solid #E6E5EA !important',
                        borderRadius: '10px',
                        padding: '16px 14px',
                        paddingRight: '0px',
                        color: theme.palette.brand.primary.base as string,
                        fontWeight: 600,
                      },
                      '& .MuiPickersArrowSwitcher-root': {
                        button: {
                          svg: {
                            color: theme.palette.text.primary,
                          },
                        },
                      },
                    },
                  },

                  '& .MuiButtonBase-root.Mui-selected': {
                    backgroundColor: theme.palette.brand.primary.base as string,
                  },
                },
                '& .MuiYearPicker-root': {
                  '& .PrivatePickersYear-root': {
                    '& .Mui-selected': {
                      backgroundColor: theme.palette.brand.primary
                        .base as string,
                    },
                  },
                },
                backgroundColor: '#F7F7F7',
                borderRadius: '10px',
                button: {
                  backgroundColor: 'transparent',
                  color: theme.palette.brand.primary[500],
                  fontWeight: 600,
                  span: {},
                },
              },
            }}
            InputAdornmentProps={{
              position: 'start',
              onClick: handleInputClick,
            }}
            renderInput={(params) => (
              <TextFieldArea className="text-field-area">
                <TextField
                  style={{
                    border: hasError ? '1px solid red' : undefined,
                    borderRadius: '10px',
                  }}
                  name={name}
                  InputLabelProps={{ shrink: false, hidden: true }}
                  error={!!(hasError || validationError)}
                  label=""
                  placeholder={placeholder}
                  {...params}
                />
              </TextFieldArea>
            )}
            onClose={handleCalendarClose}
            PopperProps={{ placement: 'bottom' }}
          />
        </LocalizationProvider>
      </div>
      {validationError && <InputError>{validationError}</InputError>}
    </CalendarInputContainer>
  );
};

export default CalendarInput;
